/**
 * Cloudinary URL optimizer.
 * Injects transformations (f_auto, q_auto, w_<width>) into any Cloudinary
 * `res.cloudinary.com/<cloud>/image/upload/...` URL so the browser receives
 * WebP/AVIF automatically at the right quality/size.
 *
 * If the URL is not a Cloudinary upload URL, it is returned unchanged.
 */
export const optimizeCloudinaryUrl = (url, opts = {}) => {
  if (!url || typeof url !== 'string') return url;
  const marker = '/image/upload/';
  const idx = url.indexOf(marker);
  if (idx === -1) return url;

  const { width, height, dpr = 'auto', crop } = opts;
  const parts = ['f_auto', 'q_auto', `dpr_${dpr}`];
  if (width) parts.push(`w_${width}`);
  if (height) parts.push(`h_${height}`);
  if (crop) parts.push(`c_${crop}`);

  const transform = parts.join(',');
  const before = url.slice(0, idx + marker.length);
  const after = url.slice(idx + marker.length);

  // Avoid double-inserting if URL already has transformations similar
  if (after.startsWith('f_auto') || after.startsWith('q_auto')) {
    return url;
  }
  return `${before}${transform}/${after}`;
};

/**
 * Convenience: returns a srcset for responsive images.
 */
export const cloudinarySrcSet = (url, widths = [400, 800, 1200, 1600]) => {
  if (!url || url.indexOf('/image/upload/') === -1) return undefined;
  return widths
    .map((w) => `${optimizeCloudinaryUrl(url, { width: w })} ${w}w`)
    .join(', ');
};
